/**
 * Reporting windows on the client - the same rules as the backend's
 * services/reporting_windows.py, so what the page shows and what the server
 * enforces never disagree. Kept pure so the arithmetic is testable.
 */

import type { MonthReview, UserRef } from '../types'
import { monthLabel } from './report'

export const DEFAULT_GRACE_DAYS = 7

export type WindowSource = 'extension' | 'window' | 'default' | 'manual'

interface WindowTiming {
  opensAt: Date
  /** Soft deadline: shown and flagged, never enforced. */
  dueAt: Date | null
  isOpen: boolean
  notYetOpen: boolean
  closed: boolean
  /** After the due instant but before the close: still editable, late. */
  pastDue: boolean
}

export interface WindowState extends WindowTiming {
  closesAt: Date
  source: Exclude<WindowSource, 'manual'>
}

/** The DDI texts on a month with a saved window. No clock closes them: a
 *  status reporting admin closes and reopens them by hand, so `closesAt` is
 *  when they were closed and null while they are open. */
export interface ManualWindowState extends WindowTiming {
  closesAt: Date | null
  source: 'manual'
  closedBy: UserRef | null
}

export type DdiWindowState = WindowState | ManualWindowState

/** A date-only string is the start of that day in UTC; anything else is a
 *  full instant. */
export function parseInstant(value: string): Date {
  return value.length === 10 ? new Date(`${value}T00:00:00Z`) : new Date(value)
}

/** The London calendar day, YYYY-MM-DD, an instant falls on. A plain date
 *  comes back as it is. */
export function londonDate(value: string): string {
  if (value.length === 10) return value
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(parseInstant(value))
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

/** The instant at which a London wall-clock time occurs. Built from a UTC
 *  guess corrected by what London's clock reads at that guess, so GMT/BST is
 *  handled without a timezone library. */
export function londonInstant(y: number, m: number, d: number, hh = 0, mm = 0, ss = 0): Date {
  const guess = Date.UTC(y, m - 1, d, hh, mm, ss)
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', hourCycle: 'h23',
    year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric',
  }).formatToParts(new Date(guess))
  const n = (type: string) => Number(parts.find(p => p.type === type)?.value)
  const seen = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour') % 24, n('minute'), n('second'))
  return new Date(guess - (seen - guess))
}

/** The rule the app always had, made explicit: the 1st of the month through
 *  the end of the 7th of the next, as UK wall-clock times - which is what
 *  "the 7th" meant when the rule lived in the browser. */
export function defaultWindow(month: string): { opens: Date; closes: Date } {
  const [y, m] = month.split('-').map(Number)
  const opens = londonInstant(y, m, 1)
  // Date.UTC normalises day overflow, so "7 days after the last day" is just
  // day 7 of the following month.
  const closes = londonInstant(y, m + 1, DEFAULT_GRACE_DAYS, 23, 59, 59)
  return { opens, closes }
}

/** Effective window for one project (or the month when no project is given).
 *  Extension beats window beats default; an extension moves only the close. */
export function resolveWindow(
  month: string,
  review?: MonthReview | null,
  projectId?: string,
  now: Date = new Date(),
): WindowState {
  const fallback = defaultWindow(month)
  const window = review?.reportingWindow ?? null
  const extension = projectId ? review?.extensions?.[projectId] : undefined

  const opensAt = window?.opensAt ? parseInstant(window.opensAt) : fallback.opens
  const dueAt = window?.dueAt ? parseInstant(window.dueAt) : null
  let closesAt: Date
  let source: WindowState['source']
  if (window?.closesAt) {
    closesAt = parseInstant(window.closesAt); source = 'window'
  } else {
    closesAt = fallback.closes; source = 'default'
  }
  // An extension can only ever lengthen: if the month's close later moves
  // past it, the month's close wins and the extension becomes moot.
  if (extension?.closesAt) {
    const extClose = parseInstant(extension.closesAt)
    if (extClose.getTime() > closesAt.getTime()) { closesAt = extClose; source = 'extension' }
  }
  return windowState(opensAt, closesAt, dueAt, source, now)
}

function windowState(opensAt: Date, closesAt: Date, dueAt: Date | null, source: WindowState['source'], now: Date): WindowState {
  const t = now.getTime()
  const closed = t > closesAt.getTime()
  return {
    opensAt, closesAt, dueAt, source,
    isOpen: opensAt.getTime() <= t && t <= closesAt.getTime(),
    notYetOpen: t < opensAt.getTime(),
    closed,
    pastDue: dueAt !== null && t > dueAt.getTime() && !closed,
  }
}

/**
 * The window for writing the DDI portfolio texts. Opens with the month's
 * window. On a month with a saved window they are due with the reports, but
 * the lock locks the status reports only: the texts stay open until a status
 * reporting admin closes them by hand. A month with no saved window closes
 * them with the reports on the default rule. Project extensions are ignored:
 * they belong to one project manager's report, while the DDI texts are one
 * lead writing across the whole portfolio.
 */
export function resolveDdiWindow(
  month: string,
  review?: MonthReview | null,
  now: Date = new Date(),
): DdiWindowState {
  const monthState = resolveWindow(month, review, undefined, now)
  const window = review?.reportingWindow
  if (!window) return monthState
  // Windows saved before the texts closed by hand may still carry a DDI
  // deadline of their own; it closes nothing.
  const t = now.getTime()
  const closesAt = window.ddiClosedAt ? parseInstant(window.ddiClosedAt) : null
  const closed = closesAt !== null
  const notYetOpen = t < monthState.opensAt.getTime()
  return {
    opensAt: monthState.opensAt, closesAt, dueAt: monthState.dueAt, source: 'manual',
    closedBy: closed ? window.ddiClosedBy ?? null : null,
    isOpen: !notYetOpen && !closed,
    notYetOpen,
    closed,
    pastDue: monthState.dueAt !== null && t > monthState.dueAt.getTime() && !closed,
  }
}

/** "Tue 7 Apr 2026, 17:00" in UK time - the audience's clock, and the same
 *  wording the backend puts in its messages and emails. */
export function fmtUk(d: Date, withTime = true, withYear = true): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: false } : {}),
    timeZone: 'Europe/London',
  }).formatToParts(d)
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? ''
  // Newer ICU data spells September "Sept"; the backend's strftime says "Sep".
  const date = `${get('weekday')} ${get('day')} ${get('month').slice(0, 3)}${withYear ? ` ${get('year')}` : ''}`
  return withTime ? `${date}, ${get('hour')}:${get('minute')}` : date
}

export function closedMessage(month: string, state: DdiWindowState): string {
  const label = monthLabel(month)
  if (state.notYetOpen) return `The ${label} reporting window opens on ${fmtUk(state.opensAt, false)}.`
  if (state.source === 'manual') {
    const who = state.closedBy?.name || state.closedBy?.email || 'a status reporting admin'
    const when = state.closesAt ? ` on ${fmtUk(state.closesAt, false)}` : ''
    return `The DDI texts for ${label} were closed by ${who}${when}. Ask a status reporting admin to reopen them.`
  }
  const what = state.source === 'extension' ? 'extension' : 'reporting window'
  return `The ${label} ${what} closed on ${fmtUk(state.closesAt)}. Ask a status reporting admin for an extension.`
}

/** Short chip text for page headers. A window with a due date walks through
 *  due, past cut-off and locked, and the DDI texts of a saved window through
 *  due, past cut-off and closed by hand; the month is on the page, so those
 *  dates drop the year. An open extension still says so, since it is why
 *  editing is open. */
export function windowChipLabel(state: DdiWindowState): string {
  if (state.notYetOpen) return `Opens ${fmtUk(state.opensAt, false)}`
  if (state.source === 'manual') {
    if (state.closed) return 'DDI texts closed'
    if (state.pastDue) return 'Past cut-off · still open'
    return state.dueAt ? `Due ${fmtUk(state.dueAt, true, false)}` : 'DDI texts open'
  }
  if (state.dueAt) {
    if (state.closed) return `Locked ${fmtUk(state.closesAt, true, false)}`
    if (state.source === 'extension') return `Extended to ${fmtUk(state.closesAt, true, false)}`
    if (state.pastDue) return `Past cut-off · editable to ${fmtUk(state.closesAt, true, false)}`
    return `Due ${fmtUk(state.dueAt, true, false)}`
  }
  if (state.closed) return `Closed ${fmtUk(state.closesAt)}`
  const verb = state.source === 'extension' ? 'Extended to' : 'Closes'
  return `${verb} ${fmtUk(state.closesAt)}`
}

/** Form helpers: the manager types a local date and time; the API stores an
 *  instant. Splitting and joining go through the browser's own zone so what
 *  they typed is what they meant. */
export function toLocalInputs(iso: string): { date: string; time: string } {
  // A plain date is a calendar day, not an instant; passing it through Date
  // would shift it by a day for anyone west of UTC.
  if (iso.length === 10) return { date: iso, time: '00:00' }
  const d = parseInstant(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  }
}

export function fromLocalInputs(date: string, time: string): string {
  return new Date(`${date}T${time || '00:00'}:00`).toISOString()
}

export type DdiDeadlineTone = 'open' | 'overdue' | 'closed' | 'waiting'

/**
 * The chip beside the DDI page's month: the deadline while the texts are open,
 * how long past it once they are overdue, and when they closed once they are.
 * A month with a saved window but no due date is simply open.
 */
export function ddiDeadlineChip(state: DdiWindowState): { label: string; tone: DdiDeadlineTone } {
  if (state.notYetOpen) return { label: `Opens ${fmtUk(state.opensAt, false)}`, tone: 'waiting' }
  if (state.closed) {
    return { label: state.closesAt ? `Closed ${fmtUk(state.closesAt, false, false)}` : 'Closed', tone: 'closed' }
  }
  if (state.dueAt) {
    const due = fmtUk(state.dueAt, true, false)
    return state.pastDue ? { label: `Due ${due} · overdue`, tone: 'overdue' } : { label: `Due ${due}`, tone: 'open' }
  }
  if (state.source !== 'manual') return { label: `Closes ${fmtUk(state.closesAt, true, false)}`, tone: 'open' }
  return { label: 'Open, no deadline set', tone: 'open' }
}
